//#region Libraries
import { useState } from "react";
//#endregion
//#region Styles
import { Container } from "./styles";
//#endregion
//#region Components
import Preloader from "components/Preloader/Preloader";
import ReservationState from "./components/ReservationState/ReservationState";
import CustomButton from "components/CustomButton/CustomButton";
import DialogAlertAction from "components/DialogAlertAction/DialogAlertAction";
import InformationBlock from "./components/InformationBlock/InformationBlock";
import ActionButtons from "./components/ActionButtons/ActionButtons";
//#endregion
//#region Services
import {
	requestCancelReservationOfResource,
	requestGetAvailableResourceToReassignment,
	requestReassignReservationOfResource
} from "services/resources/library/aboutLibraryResources";
//#endregion
//#region Utils
import useMainContext from "utils/context/useMainContext";
import useLoadingRequest from "components/Preloader/utils/hooks/useLoadingRequest";
import useShowDialogAlert from "components/DialogAlert/utils/hooks/useDialogAlert";
import { wait } from "utils/others";
import { RESOURCES_CONSTANTS } from "config/constants";
import { isPastSchedule } from "utils/date";
//#endregion

const { RESERVATION_TYPES } = RESOURCES_CONSTANTS;

const ReservationMainView = ({
	changeToListStudentReservationView,
	setMustConfirm, isGroupRoom,
	data: {
		id, title, student,
		details, location, schedule: {
			date, timeStart, timeEnd
		}, penalizeData, finalized, resource,
		dialogShowUpdate, reloadData,
		isManual
	}
}) => {
	const mainContext = useMainContext();
	//#region States
	const [resourceForReassignment, setResourceForReassignment] = useState(null);
	//#endregion
	//#region Hooks
	const loadingRequest = useLoadingRequest();
	const dialogShowResolveReassignment = useShowDialogAlert();
	const dialogShowCancelReservation = useShowDialogAlert();
	//#endregion
	//#region Functions
	const searchAvailableResource = async () => {
		loadingRequest.show("Buscando recurso...");
		try {
			const resourceAvailable = await requestGetAvailableResourceToReassignment({
				date,
				timeStart,
				timeEnd,
				resourceId: resource.id
			});
			setResourceForReassignment(resourceAvailable);
			dialogShowResolveReassignment.set(true);
		}
		catch (err) {
			// Gestionando error
			setResourceForReassignment(null);
			mainContext.setShowDialogUnexpectedError(true);
		}
		finally {
			loadingRequest.hide();
		}
	}
	const reassignReservation = async () => {
		dialogShowResolveReassignment.set(false);
		loadingRequest.show("Reasignando reserva...");
		// Realizando petición
		const success = await requestReassignReservationOfResource({
			resourceId: resourceForReassignment.id,
			reservationIdToInactive: id,
			reservationType: getReservationType()
		});
		loadingRequest.hide();
		// Gestionando respuesta
		if (!success) {
			mainContext.setShowDialogUnexpectedError(true);
			return;
		}
		// Cerrando modales
		await wait(300);
		dialogShowUpdate.toggle(false);
		await wait(300);
		// Recargando los horarios y mostrando mensaje de éxito en la reasignación
		reloadData({
			messageForToast: `¡Se reasignó exitosamente la reserva de la ${resource.name}!`
		});
	}
	const closeResolveReassignmentDialog = () => {
		dialogShowResolveReassignment.set(false);
	}
	const renderResolveReassignmentDialog = () => {
		const props = resourceForReassignment
			? {
				title: `¿Deseas reasignar la reserva del recurso ${resource.name}?`,
				content: <>
					Recuerda que la reserva del recurso <b>se reasignará al recurso {resourceForReassignment.name},</b> ya que es el próximo recurso disponible.
				</>,
				buttons: <>
					<CustomButton
						variant="secondary"
						text="No, cancelar"
						width="130px"
						onClick={closeResolveReassignmentDialog} />
					<CustomButton
						text="Sí, reasignar"
						width="130px"
						onClick={reassignReservation} />
				</>
			}
			: {
				title: `No se puede reasignar la reserva del recurso ${resource.name}`,
				content: "No se encontró ningún recurso disponible.",
				buttons: <CustomButton
					text="Cancelar"
					width="130px"
					onClick={closeResolveReassignmentDialog} />
			};
		return (
			<>
				<DialogAlertAction
					show={dialogShowResolveReassignment.value}
					toggleShow={dialogShowResolveReassignment.toggle}
					width="540px"
					widthContent="100%"
					spacingContainer="18px"
					variant="secondary"
					title={props.title}
					contentComponent={<p className="paragraph">{props.content}</p>}>
					{props.buttons}
				</DialogAlertAction>
				<Preloader {...loadingRequest.value} />
			</>
		);
	}
	const getReservationType = () => {
		return isManual
			? RESERVATION_TYPES.MANUAL
			: RESERVATION_TYPES.REGULAR;
	}
	const cancelReservation = async () => {
		dialogShowCancelReservation.set(false);
		loadingRequest.show("Cancelando reserva...");
		// Realizando petición
		const success = await requestCancelReservationOfResource({
			reservationId: id,
			reservationType: getReservationType()
		});
		loadingRequest.hide();
		// Gestionando respuesta
		if (!success) {
			mainContext.setShowDialogUnexpectedError(true);
			return;
		}
		// Cerrando modales
		await wait(300);
		dialogShowUpdate.toggle(false);
		await wait(300);
		// Mostrando mensaje de éxito en la cancelación
		reloadData({
			messageForToast: `¡Se canceló exitosamente la reserva!`
		});
	}
	//#endregion
	const hasReservationState = finalized !== null;
	return (
		<>
			<Container>
				<InformationBlock
					title={title}
					details={details}
					location={location}
					schedule={{
						date, timeStart, timeEnd
					}}
					resource={resource} />
				<ActionButtons
					disabled={finalized || isPastSchedule({ date, minutes: timeEnd })}
					searchAvailableResource={searchAvailableResource}
					cancelReservation={dialogShowCancelReservation.open}
					changeToListStudentReservationView={changeToListStudentReservationView}
					isManual={isManual}
					isGroupRoom={isGroupRoom} />
				<ReservationState
					reservation={{
						id, student: student || null,
						finalized, isManual,
						resourceTypeId: resource.type.id
					}}
					resourceName={resource.name}
					penalizeData={penalizeData}
					hasReservationState={hasReservationState}
					showUpdateDialog={dialogShowUpdate.value}
					reloadData={async message => {
						dialogShowUpdate.hide();
						await wait(350);
						reloadData({
							messageForToast: message
						});
					}}
					loadingRequest={loadingRequest}
					isGroupRoom={isGroupRoom}
					setAreThereChanges={setMustConfirm} />
			</Container>
			{dialogShowUpdate.value &&
				<>
					{renderResolveReassignmentDialog()}
					<DialogAlertAction
						show={dialogShowCancelReservation.value}
						toggleShow={dialogShowCancelReservation.toggle}
						width="640px"
						widthContent="100%"
						spacingContainer="20px"
						variant="secondary"
						title="¿Deseas cancelar esta reserva?"
						content="Recuerda que al cancelar una reserva, deberás haber coordinado previamente con el alumno.">
						<CustomButton
							variant="secondary"
							text="No, mantener"
							width="140px"
							onClick={() => dialogShowCancelReservation.set(false)} />
						<CustomButton
							text="Si, cancelar"
							width="140px"
							onClick={cancelReservation} />
					</DialogAlertAction>
				</>}
		</>
	);
}

export default ReservationMainView;